// Permission checking utility functions (ERP 1/0 permissions)
import { getErpPermissions } from "../erpClient";

// List of all available permissions/pages
export const PERMISSIONS = {
  DAILY_PRICE: "daily_price",
  CUSTOMER_ARRIVAL: "customer_arrival",
  CUSTOMER_ASSIGN: "customer_assign",
  CUSTOMER: "customer",
  BANK: "bank",
  SUPPLIER: "supplier",
  /** ERP exposes unified party access as SUPPLIER (no KARIGAR code); routes still use this key. */
  KARIGAR: "karigar",
  METAL_GROUP: "metal_group",
  ITEM_CATEGORY: "item_category",
  PRODUCT_GROUP: "product_group",
  /** ERP RBAC code: CORE_PRODUCTS (core products / product assorting CRUD) */
  PRODUCT_ASSORTING: "product_assorting",
  STOCK: "stock",
  BULK_STOCK: "bulk_stock",
  STOCK_TABLE: "stock_table",
  EDIT_STOCK: "edit_stock",
  CUSTOM_ORDER_ENTRY: "custom_order_entry",
  BILL_TYPE: "bill_type",
  /** Shop operating expenses — POST /api/shop_expenses (RBAC codes on server) */
  SHOP_EXPENSE_TABLE: "shop_expense_table",
  ADD_SHOP_EXPENSE: "add_shop_expense",
  EDIT_SHOP_EXPENSE: "edit_shop_expense",
  SALES: "sales",
  INVOICE: "invoice",
  EDIT_INVOICE: "edit_invoice",
  /** ERP permission code: ADD_SETTLEMENT */
  ADD_SETTLEMENT: "add_settlement",
  CASHBOOK_SETTLEMENT: "cashbook_settlement",
  BANKBOOK_SETTLEMENT: "bankbook_settlement",
  EMPLOYEE: "employee",
  CUSTOM_ORDER_ENTRY_TABLE: "custom_order_entry_table",
  ORDER_QUEUE: "order_queue",
  /** Backend permission code: ORDER_CANCEL → POST /api/cancel_order */
  ORDER_CANCEL: "order_cancel",
  /** Backend permission code: KARIGAR_CONVERSION → POST /api/karigar_conversion */
  KARIGAR_CONVERSION: "karigar_conversion",
  DASHBOARD: "dashboard",
  /** ERP codes: CAPITAL_INVESTMENT_TABLE, EDIT_CAPITAL_INVESTMENT */
  CAPITAL_INVESTMENT_TABLE: "capital_investment_table",
  EDIT_CAPITAL_INVESTMENT: "edit_capital_investment",
  /** ERP codes: TENANT_TABLE, EDIT_TENANT, BRANCH_TABLE, EDIT_BRANCH */
  TENANT_TABLE: "tenant_table",
  EDIT_TENANT: "edit_tenant",
  BRANCH_TABLE: "branch_table",
  EDIT_BRANCH: "edit_branch",
  OVERALL_LEDGER: "overall_ledger",
  BANK_LEDGER: "bank_ledger",
  KARIGAR_LEDGER: "karigar_ledger",
  CUSTOMER_LEDGER: "customer_ledger",
  PURCHASE_LEDGER: "purchase_ledger",
  SALES_LEDGER: "sales_ledger",
  URD_LEDGER: "urd_ledger",
  /** Day book — ERP code ROJMEL_VIEW (aliases: OVERALL_LEDGER, CASHBOOK_SETTLEMENT, CUSTOMER_LEDGER) */
  ROJMEL_VIEW: "rojmel_view",
  ADD_JANGAD: "add_jangad",
  ADD_OLD_PURCHASE: "add_old_purchase",
  ADD_PURCHASE: "add_purchase",
  REPAIRING_ORDER_ENTRY: "repairing_order_entry",
  EDIT_JANGAD: "edit_jangad",
  EDIT_OLD_PURCHASE: "edit_old_purchase",
  EDIT_PURCHASE: "edit_purchase",
  EDIT_REPAIRING_ORDER: "edit_repairing_order",
  JANGAD: "jangad",
  REFINERY: "refinery",
  ADD_REFINERY: "add_refinery",
  EDIT_REFINERY: "edit_refinery",
  METAL_PURCHASE_TABLE: "metal_purchase_table",

  PRODUCT_ASSORTING_TABLE: "product_assorting_table",
  PURCHASE_TABLE: "purchase_table",
  REPAIRING_ORDER: "repairing_order",
  REPAIRING_SALES: "repairing_sales",
  SUPPLIER_TABLE: "supplier_table",
  OLD_PURCHASE_PDF: "old_purchase_pdf",
  TEST_CUSTOMER_FORM: "test_customer_form",
  EDIT_CUSTOM_ORDER_ENTRY: "edit_custom_order_entry",
  GIRVI: "girvi",
  GIRVITABLE: "girvitable",
  ADDGIRVI: "addgirvi",
  EDITGIRVI: "editgirvi",
  GIRVIQUICKPAYMENT: "girviquickpayment",
  GIRVIBANKDETAILS: "girvibankdetails",
  GIRVIBANKLEDGER: "girvibankledger",
  /** ERP: SALES_TARGET_VIEW / SALES_TARGET_EDIT — sales targets (HRMS) */
  SALES_TARGET_VIEW: "sales_target_view",
  SALES_TARGET_EDIT: "sales_target_edit",

  /** HRMS — see `sales_targets_hrms_api.md` Part B */
  HRMS_HOLIDAY_VIEW: "hrms_holiday_view",
  HRMS_HOLIDAY_EDIT: "hrms_holiday_edit",
  HRMS_PROFILE_VIEW: "hrms_profile_view",
  HRMS_PROFILE_EDIT: "hrms_profile_edit",
  HRMS_INCENTIVE_VIEW: "hrms_incentive_view",
  HRMS_INCENTIVE_EDIT: "hrms_incentive_edit",
  HRMS_INCENTIVE_APPROVE: "hrms_incentive_approve",
  HRMS_OVERTIME_VIEW: "hrms_overtime_view",
  HRMS_OVERTIME_EDIT: "hrms_overtime_edit",
  HRMS_OVERTIME_APPROVE: "hrms_overtime_approve",
  HRMS_ADVANCE_VIEW: "hrms_advance_view",
  HRMS_ADVANCE_EDIT: "hrms_advance_edit",
  HRMS_PAYROLL_RUN: "hrms_payroll_run",
  HRMS_PAYROLL_VIEW: "hrms_payroll_view",
  HRMS_PAYROLL_LOCK: "hrms_payroll_lock",
  HRMS_ATTENDANCE_VIEW: "hrms_attendance_view",
  HRMS_ATTENDANCE_EDIT: "hrms_attendance_edit",

  /** WhatsApp Business module (ERP codes WHATSAPP_*) */
  WHATSAPP: "whatsapp",
  WHATSAPP_INBOX: "whatsapp_inbox",
  WHATSAPP_PHONEBOOK: "whatsapp_phonebook",
  WHATSAPP_CAMPAIGN: "whatsapp_campaign",
  WHATSAPP_DASHBOARD: "whatsapp_dashboard",
  WHATSAPP_TEMPLATE: "whatsapp_template",
};

/** Rojmel (day book) — primary + backend alias permissions. */
export const ROJMEL_ROUTE_ANY_PERMISSIONS = [
  PERMISSIONS.ROJMEL_VIEW,
  PERMISSIONS.OVERALL_LEDGER,
  PERMISSIONS.CASHBOOK_SETTLEMENT,
  PERMISSIONS.CUSTOMER_LEDGER,
];

/** Any HRMS permission — used for route + sidebar visibility. */
export const HRMS_ROUTE_ANY_PERMISSIONS = [
  PERMISSIONS.HRMS_HOLIDAY_VIEW,
  PERMISSIONS.HRMS_HOLIDAY_EDIT,
  PERMISSIONS.HRMS_PROFILE_VIEW,
  PERMISSIONS.HRMS_PROFILE_EDIT,
  PERMISSIONS.HRMS_INCENTIVE_VIEW,
  PERMISSIONS.HRMS_INCENTIVE_EDIT,
  PERMISSIONS.HRMS_INCENTIVE_APPROVE,
  PERMISSIONS.HRMS_OVERTIME_VIEW,
  PERMISSIONS.HRMS_OVERTIME_EDIT,
  PERMISSIONS.HRMS_OVERTIME_APPROVE,
  PERMISSIONS.HRMS_ADVANCE_VIEW,
  PERMISSIONS.HRMS_ADVANCE_EDIT,
  PERMISSIONS.HRMS_PAYROLL_RUN,
  PERMISSIONS.HRMS_PAYROLL_VIEW,
  PERMISSIONS.HRMS_PAYROLL_LOCK,
  PERMISSIONS.HRMS_ATTENDANCE_VIEW,
  PERMISSIONS.HRMS_ATTENDANCE_EDIT,
];

// Map old slug-style permission keys to new ERP UPPER_SNAKE_CASE codes where they differ
export const ERP_PERMISSION_CODE_BY_SLUG = {
  // Slugs that map 1:1 by uppercasing don't need entries here
  // Special / renamed cases:
  girvi: "GIRVI",
  girvitable: "GIRVI_TABLE",
  addgirvi: "ADD_GIRVI",
  editgirvi: "EDIT_GIRVI",
  girviquickpayment: "GIRVI_QUICK_PAYMENT",
  girvibankdetails: "GIRVI_BANK_DETAILS",
  girvibankledger: "GIRVI_BANK_LEDGER",
  karigar: "SUPPLIER",
  product_assorting: "CORE_PRODUCTS",
};

// Get all permissions from ERP auth store
export const getUserPermissions = () => {
  const perms = getErpPermissions();
  return perms && typeof perms === "object" ? perms : null;
};

// Resolve a PERMISSIONS.* slug (like "daily_price") to ERP code ("DAILY_PRICE")
export const resolveErpPermissionCode = (pageKey) => {
  const slug = String(pageKey || "").trim();
  if (!slug) return null;

  // Prefer explicit mapping when provided
  if (ERP_PERMISSION_CODE_BY_SLUG[slug]) {
    return ERP_PERMISSION_CODE_BY_SLUG[slug];
  }

  // Default: convert slug to UPPER_SNAKE_CASE (matches your ERP permission keys)
  return slug.toUpperCase();
};

/** Convert UI boolean permission map (keyed by PERMISSIONS slugs) → ERP code list. */
export const permissionBoolMapToCodes = (boolMap = {}) => {
  const codes = new Set();
  Object.entries(boolMap || {}).forEach(([slug, on]) => {
    if (!on) return;
    const code = resolveErpPermissionCode(slug);
    if (code) codes.add(code);
  });
  return Array.from(codes);
};

/** Convert ERP code list → UI boolean map keyed by PERMISSIONS slugs. */
export const permissionCodesToBoolMap = (codes = [], base = {}) => {
  const set = new Set(
    (Array.isArray(codes) ? codes : []).map((c) => String(c).toUpperCase()),
  );
  const next = { ...base };
  Object.values(PERMISSIONS).forEach((slug) => {
    const code = resolveErpPermissionCode(slug);
    next[slug] = code ? set.has(code) : false;
  });
  return next;
};

/** Convert login/me `permissions: { CODE: 0|1 }` → UI boolean map. */
export const erpPermissionsObjectToBoolMap = (erpPerms = {}, base = {}) => {
  const next = { ...base };
  Object.values(PERMISSIONS).forEach((slug) => {
    const code = resolveErpPermissionCode(slug);
    next[slug] = code ? erpPerms?.[code] === 1 : false;
  });
  return next;
};

// Check if user has permission for a specific page (ERP 1/0 flags only)
export const hasPermission = (pageKey) => {
  const perms = getErpPermissions();
  if (!perms) return false;

  const code = resolveErpPermissionCode(pageKey);
  if (!code) return false;

  const value = perms[code];
  return value === 1;
};
